import { ChangeDetectorRef, Component, ElementRef, inject, Inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import states, { State } from 'states-us';

import { BillingInfoService } from '../../models/billing-info/billing-info.service';
import { ErrorDialogService } from '../error-dialog/error-dialog.service';
import { ErrorHandler } from '../error-handler/error-handler.mixin';
import { AuthService } from '../../core/auth.service';
import { ApiHelperService } from '../../core/api-helper.service';
import { BillingInfo } from '../../models/billing-info/billing-info.model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-add-payment-method-dialog',
    templateUrl: './add-payment-method-dialog.component.html',
    styleUrls: ['./add-payment-method-dialog.component.scss'],
    standalone: false
})
export class AddPaymentMethodDialogComponent extends ErrorHandler implements OnInit, OnDestroy {
    submittingPayment = false;

    billingFormGroup!: UntypedFormGroup;
    stateOptions: State[] = [];
    cardType = 'CARD';
    achType = 'ACH';
    paymentType = this.cardType;
    collectJsFields: Record<string, { valid: boolean }> = {};

    gatewayCustomerId?: string;

    readonly formId = 'auditform';
    iFrameSrc: SafeResourceUrl | undefined;
    @ViewChild('paymentFrame') paymentFrame?: ElementRef<HTMLIFrameElement>;

    private onGatewayMessage = (event: MessageEvent) => this.handleGatewayMessage(event);

    paymentError?: string;

    stateDropdownOpen = false;

    public data = inject(MAT_DIALOG_DATA);

    constructor(
        errorDialog: ErrorDialogService,
        auth: AuthService,
        api: ApiHelperService,
        location: Location,
        public dialogRef: MatDialogRef<AddPaymentMethodDialogComponent>,
        private ngZone: NgZone,
        private fb: UntypedFormBuilder,
        private billingInfoService: BillingInfoService,
        private sanitizer: DomSanitizer,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        super(errorDialog, auth, api, location);
    }

    ngOnInit(): void {
        this.stateOptions = states.filter(s => !s.territory);

        this.billingFormGroup = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            address: ['', Validators.required],
            city: ['', Validators.required],
            state: ['', [Validators.required]],
            zip: ['', [Validators.required, Validators.pattern(/\d\d\d\d\d/)]],
            phone: ['', Validators.required] // TODOEVENTUALLY mmd - billing phone validator
        });
        window.addEventListener('message', this.onGatewayMessage);

        this.billingFormGroup.valueChanges.subscribe(() => {
            const billingInfoFields = this.billingFormGroup.value;
            if (billingInfoFields.firstName && billingInfoFields.lastName && billingInfoFields.phone && this.data.userEmail) {
                void this.loadIFrame();
            }
        });

        void this.loadIFrame();
        // TODO - leave this in for now so we can reference connecting with NMI
        // this.loadCollectJs();
    }

    get billingFirstName() { return this.billingFormGroup.get('firstName'); }
    get billingLastName() { return this.billingFormGroup.get('lastName'); }
    get billingAddress() { return this.billingFormGroup.get('address'); }
    get billingCity() { return this.billingFormGroup.get('city'); }
    get billingState() { return this.billingFormGroup.get('state'); }
    get billingZip() { return this.billingFormGroup.get('zip'); }
    get billingPhone() { return this.billingFormGroup.get('phone'); }

    isCardType() {
        return this.paymentType === this.cardType;
    }

    isAchType() {
        return this.paymentType === this.achType;
    }

    setPaymentType(type: string): void {
        if (this.paymentType === type) return;
        this.paymentType = type;
        this.iFrameSrc = undefined;
        this.loadIFrame();
        // TODO - leave this in for now so we can reference connecting with NMI
        // setTimeout(() => this.loadCollectJs(), 0);
    }

    private startCardTokenize(): void {
        const iFrameWindow = this.paymentFrame?.nativeElement.contentWindow;
        iFrameWindow?.postMessage({ action: 'updateCustomerId', value: this.gatewayCustomerId }, '*');
        iFrameWindow?.postMessage('tokenize', '*');
    }

    async loadIFrame(): Promise<void> {
        try {
            await this.ensureGatewayCustomer();
        } catch (error) {
            await this.handle(error as Error);
            return;
        }
        if (!this.gatewayCustomerId) {
            this.iFrameSrc = undefined;
            return;
        }
        const paymentType = this.isAchType() ? 'ACH' : 'Card';
        const btnTokenize = this.isAchType() ? 'on' : 'off';
        const url = `${environment.gatewayUrl}/mastercard/iframe.html` +
            `?id=${this.formId}` +
            `&mid=${environment.gatewayMerchantId}` +
            `&cid=${this.gatewayCustomerId ?? ''}` +
            `&btntokenize=${btnTokenize}` +
            `&ptype=${paymentType}`;
        this.iFrameSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        this.changeDetectorRef.detectChanges();
    }

    async enterPaymentInfo($event: Event): Promise<void> {
        if (this.billingFormGroup.invalid || this.isAchType()) {
            return;
        }
        this.submittingPayment = true;
        this.paymentError = undefined;
        try {
            await this.ensureGatewayCustomer();
            if (!this.gatewayCustomerId) {
                this.submittingPayment = false;
                return;
            }
            if (!this.iFrameSrc) {
                await this.loadIFrame();
            }
            this.startCardTokenize();
        } catch (error) {
            this.submittingPayment = false;
            await this.handle(error as Error);
        }
    }

    // TODO - leave this in for now so we can reference connecting with NMI
    // enterPaymentInfo($event: any) {
    //     if (this.billingFormGroup.invalid) {
    //         return;
    //     }
    //     this.submittingPayment = true;
    //     this.paymentFrame?.nativeElement.contentWindow?.postMessage('tokenize', '*');
    // }

    handleGatewayMessage(event: MessageEvent) {
        try {
            const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            if (data.id !== this.formId) {
                return;
            }

            if (data.message === 'tokenized' || data.message === 'ach-tokenize') {
                this.ngZone.run(() => {
                    this.submittingPayment = true;
                    this.saveBillingInfo(data.paymentMethodId);
                });
            }
            if (data.message === 'cancelled') {
                this.ngZone.run(() => this.submittingPayment = false);
            }
        } catch (error) {
            // ignore non-JSON messages
        }
    }

    saveBillingInfo(paymentMethodId: string) {
        const paymentData = {
            paymentMethodId,
            type: this.isAchType() ? 'check' : 'creditcard',
            payment: this.isAchType() ? 'check' : 'creditcard',
            organizationId: this.data.organizationId,
            subscriptionId: this.data.subscriptionId,
            company: this.data.organizationName,
            email: this.data.userEmail,
            isPrimary: true,
            country: 'US',
            gatewayCustomerId: this.gatewayCustomerId,
            ...this.billingFormGroup.value
        };

        this.billingInfoService.createBillingInfoCustomer(paymentData)
            .then((billingInfo) => {
                this.submittingPayment = false;
                this.dialogRef.close(billingInfo);
            }).catch((err: Error) => {
                this.submittingPayment = false;
                this.handle(err);
            });
    }
    
    async ensureGatewayCustomer(): Promise<void> {
        let customerId = this.data.gatewayCustomerId;
    
        if (!customerId && this.data.organizationId) {
            const billing = await this.billingInfoService
                .get({ organizationId: this.data.organizationId, isPrimary: true })
                .catch(() => null);
            customerId = billing?.gatewayCustomerId;
        }
    
        const billingFields = this.billingFormGroup.value;
        const emailAddress = this.data.userEmail;
        if (!billingFields.firstName || !billingFields.lastName || !billingFields.phone || !emailAddress) {
            return;
        }
    
        const { id } = await this.billingInfoService.createGatewayCustomer({
            customerId,
            firstName: billingFields.firstName,
            lastName: billingFields.lastName,
            phone: billingFields.phone,
            emailAddress,
        });
        this.gatewayCustomerId = id;
    }

    // loadCollectJs() {
    //     ['cc-number', 'cc-exp', 'cvv', 'check-acct', 'check-aba', 'check-name'].forEach(id => {
    //         const el = document.getElementById(id);
    //         if (el) {
    //             el.innerHTML = '';
    //         }
    //     });

    //     if (this.isAchType()) {
    //         this.collectJsFields = {
    //             checkaccount: { valid: false },
    //             checkaba: { valid: false },
    //             checkname: { valid: false },
    //         };
    //     } else {
    //         this.collectJsFields = {
    //             ccnumber: { valid: false },
    //             ccexp: { valid: false },
    //             cvv: { valid: false },
    //         };
    //     }

    //     (<any>window).CollectJS.configure({
    //         'variant': 'inline',
    //         'styleSniffer': 'true',
    //         'googleFont': 'Montserrat:400',
    //         'customCss': {
    //             'border-width': '0 0 1px',
    //             'border-color': 'rgba(0, 0, 0, 0.42)'
    //         },
    //         'invalidCss': {
    //             'color': '#F44336',
    //             'border-color': '#F44336',
    //             'border-width': '0 0 2px'
    //         },
    //         'validCss': {
    //             'color': 'black'
    //         },
    //         'placeholderCss': {
    //             'color': '#757575'
    //         },
    //         'focusCss': {
    //             'border-color': '#8FD584',
    //             'border-width': '0 0 2px'
    //         },
    //         'fields': {
    //             'checkaccount': {
    //                 'selector': '#check-acct',
    //                 'title': 'Account Number',
    //                 'placeholder': '0000000000'
    //             },
    //             'checkaba': {
    //                 'selector': '#check-aba',
    //                 'title': 'Routing Number',
    //                 'placeholder': '000000000'
    //             },
    //             'checkname': {
    //                 'selector': '#check-name',
    //                 'title': 'Name on Checking Account',
    //                 'placeholder': 'Name on Checking Account'
    //             },
    //             'ccnumber': {
    //                 'selector': '#cc-number',
    //                 'title': 'Card Number',
    //                 'placeholder': '0000 0000 0000 0000'
    //             },
    //             'ccexp': {
    //                 'selector': '#cc-exp',
    //                 'title': 'Card Expiration',
    //                 'placeholder': '00 / 00'
    //             },
    //             'cvv': {
    //                 'display': 'show',
    //                 'selector': '#cvv',
    //                 'title': 'CVV Code',
    //                 'placeholder': '***'
    //             }
    //         },
    //         'validationCallback': (field: string, status: boolean, message: string) => {
    //             if (this.collectJsFields[field] != null) {
    //                 this.collectJsFields[field].valid = status;
    //             }
    //             if (status) {
    //                 message = field + ' is now OK: ' + message;
    //             } else {
    //                 message = field + ' is now Invalid: ' + message;
    //             }
    //             // TODOLATER MMD - how to show messages PER FIELD??
    //         },
    //         'timeoutDuration': 0,
    //         'timeoutCallback': function () {
    //         },
    //         'fieldsAvailableCallback': () => {
    //             if (this.isAchType()) {
    //                 this.collectJsFields = {
    //                     checkaccount: {
    //                         valid: false
    //                     },
    //                     checkaba: {
    //                         valid: false
    //                     },
    //                     checkname: {
    //                         valid: false
    //                     }
    //                 };
    //             } else {
    //                 this.collectJsFields = {
    //                     ccnumber: {
    //                         valid: false
    //                     },
    //                     ccexp: {
    //                         valid: false
    //                     },
    //                     cvv: {
    //                         valid: false
    //                     }
    //                 };
    //             }
    //         },
    //         'callback': (response: any) => {

    //             const createData = {
    //                 payment_token: response.token,
    //                 payment: this.isAchType() ? 'check' : 'creditcard',
    //                 organizationId: this.data.organizationId,
    //                 subscriptionId: this.data.subscriptionId,
    //                 company: this.data.organizationName,
    //                 email: this.data.userEmail,
    //                 isPrimary: true,
    //                 country: 'US',
    //                 ...this.billingFormGroup.value
    //             };

    //             if (this.isAchType()) {
    //                 createData['check'] = response.check;
    //                 createData.type = 'check';
    //                 delete createData.check.hash;
    //             } else if (this.isCardType()) {
    //                 createData['card'] = response.card;
    //                 createData.type = 'creditcard';
    //                 delete createData.card.hash;
    //             }

    //             this.billingInfoService.createBillingInfoCustomer(createData)
    //                 .then((billingInfo: BillingInfo) => {
    //                     this.ngZone.run(() => {
    //                         this.submittingPayment = false;
    //                         this.dialogRef.close(billingInfo);
    //                     });
    //                 }).catch((err: Error) => {
    //                     this.ngZone.run(() => {
    //                         this.submittingPayment = false;
    //                         this.handle(err);
    //                     });
    //                 });
    //         }
    //     });
    // }

    // TODO - leave this in for now so we can reference connecting with NMI
    // enterPaymentInfo($event: any) {
    //     if (this.canSubmitCollectFields()) {
    //         this.submittingPayment = true;
    //         this.paymentError = undefined;
    //         (<any>window).CollectJS.startPaymentRequest($event);
    //     } else {
    //         this.errorDialog.open(`Invalid ${this.isAchType() ? 'ACH' : 'Card'} Information`, `One or more ${this.isAchType() ? 'ACH' : 'Card'} information fields are empty or invalid. Please fix and try again.`).subscribe(result => {
    //         });
    //     }
    // }

    canSubmitCollectFields() {
        let collectFieldsValid = true;
        for (const field in this.collectJsFields) {
            collectFieldsValid = collectFieldsValid && this.collectJsFields[field].valid;
        }
        return collectFieldsValid;
    }

    selectState(abbreviation: string): void {
        this.billingFormGroup.get('state')?.setValue(abbreviation);
        this.stateDropdownOpen = false;
    }

    getStateName(abbreviation: string): string {
        return this.stateOptions.find(s => s.abbreviation === abbreviation)?.name ?? '';
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.onGatewayMessage);
    }

}
